import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Lottie from "lottie-react";
import businessAnimation from "../api/Business Analysis.json";

import { useAuth } from "../context/AuthContext";
import { useFeedback } from "../components/ActionFeedback";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { showToast } = useFeedback();

  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) return showToast("Please fill all fields", "error");
    
    setIsSubmitting(true);
    try {
      await login(email, password);
      showToast("Log in successful. Redirecting...", "success");
      navigate("/dashboard");
    } catch (err) {
      showToast(err.response?.data?.message || "Login failed. Check credentials.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#050508] flex items-center justify-center p-4 sm:p-8 font-sans relative overflow-hidden text-slate-100">
      
      {/* Dynamic Background Ambient Light */}
      <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-amber-500/10 rounded-full blur-[160px] animate-pulse pointer-events-none" style={{ animationDuration: '10s' }}></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-indigo-500/10 rounded-full blur-[140px] pointer-events-none"></div>

      {/* Grid Overlay Texture (Subtle Grain/Dots) */}
      <div className="absolute inset-0 pointer-events-none z-0 opacity-20 mix-blend-overlay" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

      {/* UNIFIED GLASS PLATE CONTAINER */}
      <div className="w-full max-w-[1100px] flex flex-col md:flex-row relative z-10 mx-auto min-h-[650px] bg-white/[0.02] border border-white/[0.05] rounded-[3rem] backdrop-blur-[40px] shadow-[0_20px_80px_rgba(0,0,0,0.8)] overflow-hidden">
        
        {/* Subtle top-light gradient spanning the entire unified card */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-[80%] h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent z-20"></div>
        
        {/* LEFT COMPONENT - Unified Visual Area */}
        <div className="hidden md:flex flex-1 relative justify-center items-center p-8 group">
          <div className="relative flex justify-center items-center w-full h-full translate-x-8 lg:translate-x-10">
            {/* Internal Unified Glow flowing under the graphic */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-400/10 rounded-full filter blur-[100px] group-hover:bg-amber-400/20 transition-all duration-[2000ms]"></div>
            
            <div className="relative w-full max-w-lg lg:max-w-xl flex items-center justify-center transition-transform duration-[1500ms] ease-out group-hover:scale-[1.05]">
              <Lottie animationData={businessAnimation} loop={true} className="w-full h-full object-contain scale-[1.3] lg:scale-[1.4] drop-shadow-[0_0_30px_rgba(255,255,255,0.05)] relative z-10" />
            </div>
          </div>

          {/* Decorative Tag */}
          <div className="absolute bottom-10 left-10 flex gap-3 text-xs font-bold tracking-widest text-slate-500 uppercase items-center z-20">
             <div className="h-[1px] w-8 bg-slate-500/50"></div>
            Admin Portal
          </div>
        </div>

        {/* RIGHT COMPONENT - Seamless Login Form */}
        <div className="flex-[0.8] w-full flex flex-col justify-center px-10 py-16 lg:px-14 relative z-10">
          
          <div className="mb-14">
            <h2 className="text-[2.6rem] font-black leading-[1.05] tracking-tighter text-white mb-3">
              Hello,<br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-amber-500">Welcome back.</span>
            </h2>
            <p className="text-[13px] text-slate-400 font-medium">Log in to manage configurations and view insights.</p>
          </div>
          
          <div className="space-y-6 w-full max-w-sm mx-auto md:mx-0">
            
            {/* Username/Email Input */}
            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 pl-1">Email Address</label>
              <input
                type="email"
                className="w-full bg-[#0d0d12]/50 border border-white/5 rounded-2xl py-4 px-5 text-white outline-none text-[15px] placeholder:text-slate-600 font-medium focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20 focus:bg-[#0d0d12] transition-all"
                placeholder="admin@example.com"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            
            {/* Password Input */}
            <div className="space-y-2 pt-2">
               <div className="flex justify-between items-end pl-1">
                 <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Password</label>
                 {/* <span className="text-[10px] font-bold text-slate-400 hover:text-amber-400 cursor-pointer transition-colors tracking-wide">
                    Forgot password?
                 </span> */}
               </div>
               <div className="relative group">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full bg-[#0d0d12]/50 border border-white/5 rounded-2xl py-4 px-5 pr-14 text-white outline-none text-[15px] placeholder:text-slate-600 font-medium focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20 focus:bg-[#0d0d12] transition-all"
                  placeholder="••••••••"
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-amber-400 transition-all p-2 focus:outline-none"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                  )}
                </button>
               </div>
            </div>
            
            {/* Login Button */}
            <button
              onClick={handleLogin}
              disabled={isSubmitting}
              className={`w-full mt-8 ${isSubmitting ? 'bg-amber-500/50 cursor-not-allowed' : 'bg-amber-500 hover:bg-amber-400'} text-[#050508] py-4 rounded-2xl font-black tracking-widest uppercase text-[13px] shadow-[0_0_40px_rgba(245,158,11,0.2)] hover:shadow-[0_0_60px_rgba(245,158,11,0.4)] transition-all active:scale-[0.98] border border-transparent overflow-hidden relative group`}
            >
              <div className="absolute inset-0 w-full h-full bg-white/30 skew-x-[-25deg] -translate-x-[150%] group-hover:transition-transform group-hover:duration-700 group-hover:translate-x-[150%]"></div>
              <span>{isSubmitting ? "Authenticating..." : "Secure Login"}</span>
            </button>
            
            <div className="mt-8 text-center">
              <Link 
                to="/delete-account" 
                className="text-[10px] font-bold text-slate-500 hover:text-amber-500 transition-colors tracking-widest uppercase"
              >
                Request Account Deletion
              </Link>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
}